import {useCallback, useContext, useEffect, useMemo} from 'react'

import {getBuildChangesArg, getChangesArg} from '../../../../rest/arguments'
import {getOverviewHref} from '../../../../routes'
import {getBuild, getBuildType} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import {internalProps} from '../../../../types/BS_types'
import {subscribeOnBuildTypeEvents} from '../../../../utils/subscriber'
import {BUILD_CHANGES_LOADED} from '../../../../utils/subscriptionEvents'
import {SequenceLoaderContext} from '../../../common/SequenceLoader/SequenceLoader.hooks'
import {ChangeContext, ChangeView, DEFAULT_CHANGES_LIST_CONTEXT} from '../Change/Change.context'
import {
  getArtifactChangesCount,
  getBuildChangesCount,
  getFirstBuildIdWithSameChanges,
} from '../Changes.selectors'
import {getChangesLocator} from '../Changes.utils'

import ChangesPreview from './ChangesPreview'
import type {ContainerProps} from './ChangesPreview.types'

import {useAppSelector} from 'src/hooks/react-redux'

const CHANGES_LIMIT = Number(internalProps['teamcity.internal.buildChangesPopupLimit'])

function ChangesPreviewContainer(props: ContainerProps) {
  const {buildId, buildTypeId, withChangesActions, withExpandableLines} = props

  const history = useAppSelector(state => getBuild(state, buildId)?.history)
  const queued = useAppSelector(state => getBuild(state, buildId)?.state === 'queued')
  const running = useAppSelector(state => getBuild(state, buildId)?.state === 'running')
  const buildTypeInternalId = useAppSelector(state => getBuildType(state, buildTypeId)?.internalId)
  const baseChangesTabLink = `${getOverviewHref({buildId, buildTypeId})}?buildTab=changes`

  const locator = getChangesLocator({
    baseLocator: `build:(id:${stringifyId(buildId)})`,
    count: CHANGES_LIMIT,
  })

  const changesContextValue = useMemo(
    () => ({
      ...DEFAULT_CHANGES_LIST_CONTEXT,
      changeView: ChangeView.PREVIEW,
      withGraph: false,
      withChangesActions,
      withExpandableLines,
    }),
    [withChangesActions, withExpandableLines],
  )
  const {showFiles} = changesContextValue

  const [
    fetchChangesCounts,
    {changesCount, artifactChangesCount, isReadyCounts, firstBuildIdWithSameChanges},
  ] = restApi.endpoints.getBuildNormalized.useLazyQuery({
    selectFromResult: ({data}) => ({
      changesCount: getBuildChangesCount(data),
      artifactChangesCount: getArtifactChangesCount(data),
      firstBuildIdWithSameChanges: getFirstBuildIdWithSameChanges(data),
      isReadyCounts: data != null,
    }),
  })

  const [fetchChanges] = restApi.endpoints.getAllChanges.useLazyQuerySubscription()
  const fetch = useCallback(() => {
    if (buildId != null) {
      fetchChanges(getChangesArg(locator, {withFiles: showFiles}))
      fetchChangesCounts(getBuildChangesArg({buildId, history}))
    }
  }, [buildId, fetchChanges, fetchChangesCounts, history, locator, showFiles])

  useEffect(() => {
    if (buildTypeInternalId != null && (queued || running)) {
      return subscribeOnBuildTypeEvents(buildTypeInternalId, [BUILD_CHANGES_LOADED], fetch)
    } else {
      fetch()
      return undefined
    }
  }, [buildTypeInternalId, fetch, queued, running])

  const {onReady} = useContext(SequenceLoaderContext)
  useEffect(() => {
    if (onReady != null && isReadyCounts && changesCount === 0 && artifactChangesCount === 0) {
      onReady()
    }
  }, [artifactChangesCount, changesCount, isReadyCounts, onReady])

  return isReadyCounts ? (
    <ChangeContext.Provider value={changesContextValue}>
      <ChangesPreview
        {...props}
        locator={locator}
        baseChangesTabLink={baseChangesTabLink}
        changesCount={changesCount}
        artifactChangesCount={artifactChangesCount}
        firstBuildIdWithSameChanges={firstBuildIdWithSameChanges}
        withSinceLastSuccessfulBuild
      />
    </ChangeContext.Provider>
  ) : null
}

export default ChangesPreviewContainer
